import {
  Box,
  Button,
  Center,
  Flex,
  Group,
  Paper,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { Goal, GoalTimestamp } from "../../types/api";
import { ToLeftIcon, ToRightIcon } from "../../shared/svg";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useRef, useState } from "react";
import BotMessageSquareIcon from "../../shared/svg/BotMessage";
import { GoalItem } from "../../features/calendar/calendar-utils/GoalItem";
import { AITooltip } from "../../utils/components/AITooltip";
import { IconPlus } from "../../shared/svg/AddIcon";
import { OnboardingTour } from "@gfazioli/mantine-onboarding-tour";
import useProfileStore from "../../store/useProfileStore";

export interface PositionedTask {
  task: GoalTimestamp;
  startIdx: number;
  dayRange: number;
  layer: number;
}

export type CalendarViewProps = {
  currentMonth: Dayjs;
  changeMonth: (direction: "prev" | "next") => void;
  daysInMonth?: number[];
  goals: Goal[];
  rowHeights: number[];
  taskRowRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  commonStyles: React.CSSProperties;

  setIsOpen: (value: boolean) => void;
  setIsEditOpen: (value: boolean) => void;
  setEditGoal: (goal: Goal | null) => void;

  setIsTaskOpen: (value: boolean) => void;
  setSelectedDate: (date: Dayjs | null) => void;

  setIsEditTaskOpen: (value: boolean) => void;
  setEditTask: (task: GoalTimestamp | null) => void;
};

export default function CalendarMonthView({
  currentMonth,
  changeMonth,
  daysInMonth,
  goals,
  rowHeights,
  taskRowRefs,
  commonStyles,
  setIsOpen,
  setIsEditOpen,
  setEditGoal,
  setIsTaskOpen,
  setSelectedDate,
  setIsEditTaskOpen,
  setEditTask,
}: CalendarViewProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const isTouchDevice =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  const [hovered, setHovered] = useState<"main" | "ai" | null>(null);
  const matches = useMediaQuery("(min-width: 990px)");
  const todayRef = useRef<HTMLDivElement>(null);
  const firstDayRef = useRef<HTMLDivElement>(null);
  const { profile } = useProfileStore();

  useEffect(() => {
    if (!matches) return;
    if (!profile?.display_preferences.hide_tour_tooltip_on_dev_plan) return;

    if (todayRef.current) {
      todayRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    } else if (firstDayRef.current) {
      firstDayRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [
    matches,
    currentMonth,
    profile?.display_preferences.hide_tour_tooltip_on_dev_plan,
  ]);

  useEffect(() => {
    if (!matches) return;
    if (
      firstDayRef.current &&
      profile?.display_preferences.hide_tour_tooltip_on_dev_plan !==
        undefined &&
      !profile?.display_preferences.hide_tour_tooltip_on_dev_plan
    ) {
      firstDayRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [profile?.display_preferences.hide_tour_tooltip_on_dev_plan, matches]);

  return (
    <Box>
      <Group gap={24} align="center">
        <OnboardingTour.Target id="calendar-period">
          <Flex gap={24} align="center" id="calendar-period" px={5}>
            <span
              onClick={() => changeMonth("prev")}
              style={{ cursor: "pointer" }}
            >
              <ToLeftIcon />
            </span>
            <Title style={{ textTransform: "capitalize" }} size={24}>
              {currentMonth.format("MMMM")}
            </Title>
            <span
              onClick={() => changeMonth("next")}
              style={{ cursor: "pointer" }}
            >
              <ToRightIcon />
            </span>
          </Flex>
        </OnboardingTour.Target>
      </Group>
      <Paper className="calendar" withBorder radius={16} mt={32}>
        {/* Goals */}
        <Box w="276px" style={{ borderRight: "1px solid #d6d6d6" }}>
          <Text px={11} py={16} c="#737373" h={58} className="goal-item">
            {t("goals")}
          </Text>
          {goals.map((goal, index) => {
            return index === 0 ? (
              <OnboardingTour.Target id="calendar-goal">
                <div key={goal.id} id="calendar-goal">
                  <GoalItem
                    goal={goal}
                    height={rowHeights[index] || 58}
                    onClickTitle={(goal) => {
                      setEditGoal(goal);
                      setIsEditOpen(true);
                    }}
                    onClickAddTask={(goal) => {
                      setEditGoal(goal);
                      setIsTaskOpen(true);
                    }}
                  />
                </div>
              </OnboardingTour.Target>
            ) : (
              <GoalItem
                goal={goal}
                key={goal.id}
                height={rowHeights[index] || 58}
                onClickTitle={(goal) => {
                  setEditGoal(goal);
                  setIsEditOpen(true);
                }}
                onClickAddTask={(goal) => {
                  setEditGoal(goal);
                  setIsTaskOpen(true);
                }}
              />
            );
          })}

          <Box px={11}>
            <OnboardingTour.Target id="calendar-add-goal">
              <Flex
                id="calendar-add-goal"
                className="goal-input"
                w="254px"
                align="center"
                gap={8}
              >
                <Button
                  variant="outline"
                  radius="xl"
                  onClick={() => setIsOpen(true)}
                  style={{
                    width: "100%",
                    padding: "8px 12px 0 0",
                    border: "transparent",
                    background: "transparent",
                    color: "#737373",
                    display: "flex",
                    fontWeight: 400,
                  }}
                >
                  <label htmlFor="goal" className="goal-icon">
                    +
                  </label>{" "}
                  {t("add_goal")}
                </Button>
              </Flex>
            </OnboardingTour.Target>
          </Box>
        </Box>

        {/* Calendar */}
        <Box className="days">
          <Flex w="max-content">
            {daysInMonth?.map((date, index) => {
              const thisDay = currentMonth.date(date);
              const today = dayjs();
              const isSameMonth =
                currentMonth.isSame(today, "month") &&
                currentMonth.isSame(today, "year");
              const isToday = isSameMonth && thisDay.isSame(today, "day");
              const isFirst = index === 0;

              const day = thisDay.format("ddd");
              const number = thisDay.format("D");

              return (
                <Flex
                  key={index}
                  ref={isToday ? todayRef : isFirst ? firstDayRef : null}
                  w={94}
                  h={58}
                  align="center"
                  justify="center"
                  className="day"
                  direction="column"
                  c={isToday ? "#fff" : "#737373"}
                  bg={isToday ? "#6F40E2" : "transparent"}
                  style={{
                    borderRadius: isToday ? 12 : 0,
                    fontWeight: isToday ? 700 : 400,
                  }}
                >
                  <Text style={{ textTransform: "capitalize" }}>{day}</Text>
                  <Text>{number}</Text>
                </Flex>
              );
            })}
          </Flex>

          <OnboardingTour.Target id="calendar-tasks">
            <div>
              {goals?.map((goal, index) => {
                const monthStart = currentMonth.startOf("month");
                const monthEnd = currentMonth.endOf("month");
                const daySlots: GoalTimestamp[][] = daysInMonth!.map(() => []);
                const allTasks: GoalTimestamp[] = [];

                goal.tasks.forEach((task) => {
                  const start = dayjs(task.day_from);
                  const end = dayjs(task.day_to);
                  if (end.isBefore(monthStart) || start.isAfter(monthEnd))
                    return;

                  const clippedStart = start.isBefore(monthStart)
                    ? monthStart
                    : start;
                  const clippedEnd = end.isAfter(monthEnd) ? monthEnd : end;

                  allTasks.push({
                    ...task,
                    day_from: clippedStart.toISOString(),
                    day_to: clippedEnd.toISOString(),
                  });

                  const startIdx = clippedStart.date() - 1;
                  const endIdx = clippedEnd.date() - 1;

                  for (let i = startIdx; i <= endIdx; i++) {
                    if (!daySlots[i]) {
                      daySlots[i] = [];
                    }
                    daySlots[i].push(task);
                  }
                });

                const sortedTasks = allTasks.sort((a, b) =>
                  dayjs(a.day_from).diff(dayjs(b.day_from))
                );
                const taskLayers: GoalTimestamp[][] = [];
                const positionedTasks: PositionedTask[] = sortedTasks.map(
                  (task) => {
                    const startIdx = dayjs(task.day_from).date() - 1;
                    const endIdx = dayjs(task.day_to).date() - 1;
                    const dayRange = endIdx - startIdx + 1;

                    let layer = 0;
                    while (
                      taskLayers[layer]?.some((t) => {
                        const tStart = dayjs(t.day_from).date() - 1;
                        const tEnd = dayjs(t.day_to).date() - 1;
                        return startIdx <= tEnd && endIdx >= tStart;
                      })
                    ) {
                      layer++;
                    }

                    taskLayers[layer] = [...(taskLayers[layer] || []), task];
                    return { task, startIdx, dayRange, layer };
                  }
                );

                const rowHeight = Math.max((taskLayers.length + 1) * 30, 58);

                return (
                  <Box
                    key={goal.id}
                    w="max-content"
                    id={index === 0 ? "calendar-tasks" : undefined}
                    className="row"
                    ref={(el) => (taskRowRefs.current[index] = el)}
                    style={{ position: "relative", height: rowHeight }}
                  >
                    {/* Фонова сітка */}
                    <Flex style={{ position: "absolute" }}>
                      {daysInMonth!.map((_, date) => (
                        <Center
                          key={date}
                          onClick={() => {
                            setSelectedDate(currentMonth.date(date + 1));
                            setEditGoal(goal);
                            setIsTaskOpen(true);
                          }}
                          style={{
                            cursor: "pointer",
                          }}
                          w={94}
                          h={rowHeight}
                          className="day"
                        >
                          {daySlots[date].length === 0 && (
                            <Text
                              c="#00000034"
                              w="30px"
                              h="30px"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                            >
                              <IconPlus />
                            </Text>
                          )}
                        </Center>
                      ))}
                    </Flex>

                    {/* Таски */}
                    {positionedTasks.map(
                      ({ task, startIdx, dayRange, layer }) => (
                        <Box
                          key={task.id}
                          onClick={() => {
                            const originalTask = goal.tasks.find(
                              (t) => t.id === task.id
                            );
                            if (originalTask) {
                              setEditGoal(goal);
                              setEditTask(originalTask);
                              setIsEditTaskOpen(true);
                            }
                          }}
                          style={{
                            position: "absolute",
                            top: layer * 30,
                            left: startIdx * 94,
                            width: 94 * dayRange,
                            height: 28,
                            backgroundColor: task.display_color_rgba,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 4,
                            border: "1px solid #ccc",
                            cursor: "pointer",
                          }}
                          onMouseEnter={() => setHovered("main")}
                          onMouseLeave={() => setHovered(null)}
                        >
                          <Tooltip.Floating
                            label={task.title}
                            position="top"
                            offset={6}
                            disabled={isTouchDevice || hovered === "ai"}
                            styles={{
                              tooltip: {
                                ...commonStyles,
                                border: "1px solid gray",
                              },
                              arrow: {
                                backgroundColor: theme.colors.gray[1],
                                border: "1px solid gray",
                              },
                            }}
                          >
                            <Flex
                              align="center"
                              justify="center"
                              gap={4}
                              style={{ overflow: "hidden", flex: 1 }}
                            >
                              {task.autogenerated && (
                                <AITooltip
                                  icon={
                                    <Box
                                      w={24}
                                      h={24}
                                      ml={4}
                                      style={{
                                        borderRadius: "50%",
                                        backgroundColor: "white",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                      }}
                                      onMouseEnter={(e) => {
                                        e.stopPropagation();
                                        setHovered("ai");
                                      }}
                                      onMouseLeave={() => setHovered(null)}
                                    >
                                      <BotMessageSquareIcon
                                        size={16}
                                        color={theme.colors.violet[7]}
                                      />
                                    </Box>
                                  }
                                  title={t("generated_by_ai")}
                                />
                              )}
                              <Text
                                style={{
                                  fontSize: 12,
                                  padding: "2px 4px",
                                  width: "100%",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                  textAlign: "center",
                                }}
                              >
                                {task.title}
                              </Text>
                            </Flex>
                          </Tooltip.Floating>
                        </Box>
                      )
                    )}

                    {/* Додатковий шар з "+" лише для днів з тасками */}
                    <Flex
                      style={{
                        position: "absolute",
                        top: taskLayers.length * 30,
                      }}
                    >
                      {daysInMonth!.map((_, dateIdx) => {
                        const tasksThatDay = daySlots[dateIdx] || [];

                        if (tasksThatDay.length === 0) {
                          return (
                            <Box
                              key={`empty-${dateIdx}`}
                              w={94}
                              h={28}
                              onClick={() => {
                                setSelectedDate(currentMonth.date(dateIdx + 1));
                                setEditGoal(goal);
                                setIsTaskOpen(true);
                              }}
                              style={{
                                cursor: "pointer",
                              }}
                            />
                          );
                        }

                        return (
                          <Center
                            key={`plus-${dateIdx}`}
                            onClick={() => {
                              setSelectedDate(currentMonth.date(dateIdx + 1));
                              setEditGoal(goal);
                              setIsTaskOpen(true);
                            }}
                            style={{
                              cursor: "pointer",
                            }}
                            w={94}
                            h={28}
                          >
                            <Text
                              c="#00000034"
                              w="30px"
                              h="30px"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                                fontSize: 16,
                              }}
                            >
                              <IconPlus />
                            </Text>
                          </Center>
                        );
                      })}
                    </Flex>
                  </Box>
                );
              })}
            </div>
          </OnboardingTour.Target>

          <Flex w="max-content">
            {daysInMonth!.map((_, index) => (
              <Box w={94} h={58} className="day" key={index} />
            ))}
          </Flex>
        </Box>
      </Paper>
    </Box>
  );
}
